import { api, INVALIDATION_TAGS } from 'reduxStore/api';
import { GEOCODE_URL } from 'configs/urls';
import {
  GET_CURRENT_LANG_DICTIONARY_ITEM,
  GET_FIELD_LIST,
  GET_LAYOUT_ELEMENTS,
  GET_MENU_BY_ROLE,
  SAVE_FIELD,
  SAVE_LAYOUT_ELEMENT,
} from 'constants/api';

const getGeolocation = () =>
  new Promise((resolve, reject) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(({ coords }) => {
        resolve(coords);
      }, reject);
    }
  });

const geolocationBaseQuery = async () => {
  const coords = await getGeolocation();

  return { data: coords };
};
const layoutElementsWithChangedEnabled = (dispatch, layoutElementId, isEnabled) => {
  dispatch(
    settingsApi.util.updateQueryData('getLayoutElement', undefined, draft => {
      draft.result = draft.result.map(item =>
        item.layoutElementId === layoutElementId ? { ...item, isEnabled: isEnabled } : item
      );
    })
  );
};

export const settingsApi = api.injectEndpoints({
  endpoints: builder => ({
    getCountryByGeolocation: builder.query({
      queryFn: async () => {
        try {
          const { data: coordinatesResult } = await geolocationBaseQuery();
          const { latitude, longitude } = coordinatesResult || {};

          const params = new URLSearchParams({ latitude, longitude }).toString();
          const url = `${GEOCODE_URL}?${params}`;

          const response = await fetch(url, { method: 'GET' });
          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: error.message };
        }
      },
    }),
    getLayoutElement: builder.query({
      query: body => ({
        url: GET_LAYOUT_ELEMENTS,
        method: 'POST',
        body,
      }),
      transformResponse: response => {
        const { result } = response;

        return {
          result: result.map(item => ({
            ...item,
            isEnabled: Boolean(Number(item.isEnabled)),
            layoutElementId: Number(item.layoutElementId),
          })),
        };
      },
    }),
    saveLayoutElements: builder.mutation({
      query: body => ({
        url: SAVE_LAYOUT_ELEMENT,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { layoutElementId, isEnabled } = body?.PartnerLayoutElement ?? {};
        layoutElementsWithChangedEnabled(dispatch, layoutElementId, isEnabled);
        const { data } = await queryFulfilled;

        if (!data?.result) {
          layoutElementsWithChangedEnabled(dispatch, layoutElementId, !isEnabled);
        }
      },
    }),
    getCurrentLangDictionaryItem: builder.query({
      query: body => ({
        url: GET_CURRENT_LANG_DICTIONARY_ITEM,
        method: 'POST',
        body,
      }),
      providesTags: [INVALIDATION_TAGS.GET_CURRENT_LANG_DICTIONARY_ITEM],
    }),
    getMenuByRole: builder.query({
      query: body => ({
        url: GET_MENU_BY_ROLE,
        method: 'POST',
        body,
      }),
    }),
    getFieldList: builder.query({
      query: body => ({
        url: GET_FIELD_LIST,
        method: 'POST',
        body,
      }),
      providesTags: [INVALIDATION_TAGS.GET_FIELD_LIST],
    }),
    saveField: builder.mutation({
      query: body => ({
        url: SAVE_FIELD,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        if (data?.result) {
          dispatch(api.util.invalidateTags([INVALIDATION_TAGS.GET_FIELD_LIST]));
        }
      },
    }),
  }),
});

export const {
  useGetCountryByGeolocationQuery,
  useGetLayoutElementQuery,
  useSaveLayoutElementsMutation,
  useGetCurrentLangDictionaryItemQuery,
  useGetMenuByRoleQuery,
  useGetFieldListQuery,
  useSaveFieldMutation,
} = settingsApi;
